import { memo, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { StaticImageData } from 'next/image';
import classnames from 'classnames';
import gsap from 'gsap';
import { A11y, Swiper as SwiperClass } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';

import styles from './CommunityCarousel.module.scss';

import BaseImage from '@/components/BaseImage/BaseImage';
import BaseLink from '@/components/BaseLink/BaseLink';
import Cta from '@/components/Cta/Cta';
import ProgressNavigationArrow from '@/components/ProgressNavigationArrow/ProgressNavigationArrow';

import { useLayout } from '@/hooks';
import { getPropertyValue } from '@/utils/basic-functions';

export type Props = {
  className?: string;
  title: string;
  data: {
    id: number;
    image: {
      data: StaticImageData;
      alt: string;
    };
    title: string;
    description: string;
    cta: string;
  }[];
  carouselCtaText: string;
  cta: {
    href: string;
    text: string;
  };
};

function CommunityCarousel({ className, title, data, carouselCtaText, cta }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const swiperRef = useRef<SwiperClass | null>(null);
  const [slideCounter, setSlideCounter] = useState(1);
  const counterRef = useRef<HTMLSpanElement[]>([]);
  const navProgressPercentage = (slideCounter / data.length) * 100;
  const previousSlide = useRef(0);
  const [scrollBar, setScrollBar] = useState(false);
  const { layout } = useLayout();

  useEffect(() => {
    const timeOut = setTimeout(() => {
      const scrollbarWidth = getPropertyValue('--scrollbar-width');
      setScrollBar(Boolean(parseInt(scrollbarWidth)));
    }, 50);
    return () => clearTimeout(timeOut);
  }, []);

  useLayoutEffect(() => {
    if (swiperRef.current) {
      gsap.to(counterRef.current[swiperRef.current.realIndex], { opacity: 1 });
      const inactiveSlide = containerRef.current!.querySelectorAll(
        '.swiper-slide-prev, .swiper-slide-next, .swiper-slide-duplicate-next, .swiper-slide-duplicate-prev, .swiper-slide:not(.swiper-slide-active)'
      );
      const inactiveContentWrapper = Array.from(inactiveSlide!).map((el) => {
        return el.querySelector(`.${styles.contentWrapper}`);
      });
      if (layout.desktopMd) {
        gsap.to(inactiveSlide, { scale: 0.76, transformOrigin: 'top left' });
        gsap.to(inactiveContentWrapper, { opacity: 0 });
      } else {
        gsap.to(inactiveSlide, { scale: 1 });
        gsap.to(inactiveContentWrapper, { opacity: 1 });
      }
    }
  }, [layout.desktopMd]);

  useEffect(() => {
    swiperRef.current &&
      swiperRef.current.on('slideChangeTransitionStart', () => {
        const activeSlide = containerRef.current!.querySelector('.swiper-slide-active');
        const inactiveSlide = containerRef.current!.querySelectorAll(
          '.swiper-slide-prev, .swiper-slide-next, .swiper-slide-duplicate-next, .swiper-slide-duplicate-prev, .swiper-slide:not(.swiper-slide-active)'
        );
        const activeContentWrapper = activeSlide!.querySelector(`.${styles.contentWrapper}`);
        const inactiveContentWrapper = Array.from(inactiveSlide!).map((el) => {
          return el.querySelector(`.${styles.contentWrapper}`);
        });
        const matchMedia = gsap.matchMedia();
        matchMedia.add('(min-width: 1150px)', () => {
          // this setup code only runs when viewport is at desktopMd bp
          gsap.to(activeSlide, { duration: 1.93, ease: 'ease02', scale: 1 });
          gsap.to(inactiveSlide, { duration: 1.93, ease: 'ease02', scale: 0.76, transformOrigin: 'top left' });
          gsap.to(activeContentWrapper, {
            opacity: 1,
            ease: 'ease01',
            duration: 1.26,
            delay: 0.43,
            pointerEvents: 'auto'
          });
          gsap.to(inactiveContentWrapper, {
            opacity: 0,
            ease: 'ease01',
            duration: 1.26,
            delay: 0.03,
            pointerEvents: 'none'
          });
          return () => {
            gsap.to(inactiveSlide, { scale: 1 });
            gsap.to(inactiveContentWrapper, { opacity: 1 });
          };
        });
      });
  }, []);

  const handleSlideChange = useCallback(() => {
    if (swiperRef.current !== null) {
      gsap.fromTo(
        counterRef.current[previousSlide.current],
        { duration: 1.73, y: 0, ease: 'ease02', opacity: 1 },
        { y: -19.5, opacity: 0 }
      );
      setSlideCounter(swiperRef.current.realIndex + 1);
      gsap.fromTo(
        counterRef.current[swiperRef.current.realIndex],
        { duration: 1.73, y: 19.5, ease: 'ease02', opacity: 0 },
        { y: 0, opacity: 1 }
      );
      previousSlide.current = swiperRef.current.realIndex;
    }
  }, []);

  return (
    <div className={classnames(styles.CommunityCarousel, className)}>
      <div className={styles.headingWrapper}>
        <h2 className={styles.heading}>{title}</h2>
        <div className={styles.navigation}>
          <span className={styles.slideCounter}>
            {data.map((_, i) => (
              <span ref={(ref) => counterRef.current.push(ref!)} className={styles.counter} key={i}>
                {`0${[i + 1]}`}
              </span>
            ))}
            <span className={styles.totalSlides}> {` -  0${data.length}`}</span>
          </span>
          <ProgressNavigationArrow
            className={styles.desktopNavArrow}
            onLeftClick={() => swiperRef.current?.slidePrev()}
            onRightClick={() => swiperRef.current?.slideNext()}
            percentage={navProgressPercentage}
          />
        </div>
      </div>
      <div className={classnames(styles.carouselWrapper, { [styles.scrollBar]: scrollBar })} ref={containerRef}>
        <Swiper
          modules={[A11y]}
          className={styles.swiper}
          onBeforeInit={(swiper) => {
            swiperRef.current = swiper;
          }}
          onSlideChange={handleSlideChange}
          shortSwipes={true}
          speed={1930}
          slidesPerView="auto"
          slidesOffsetBefore={0}
          edgeSwipeThreshold={0}
        >
          {data.map((slide, i) => (
            <SwiperSlide key={i} className={styles.swiperSlide}>
              <BaseImage data={slide.image.data} alt={slide.image.alt} />
              <div className={styles.contentWrapper}>
                <h3 className={styles.title}>{slide.title}</h3>
                <p className={styles.description}>{slide.description}</p>
                <Cta className={styles.cta} title={carouselCtaText} href={slide.cta}></Cta>
              </div>
            </SwiperSlide>
          ))}
          <hr className={styles.line} />
        </Swiper>
      </div>
      <div className={styles.explore}>
        <ProgressNavigationArrow
          className={styles.mobileNavArrow}
          onLeftClick={() => swiperRef.current?.slidePrev()}
          onRightClick={() => swiperRef.current?.slideNext()}
          percentage={navProgressPercentage}
        />
        <BaseLink title={cta.text} href={cta.href} className={styles.cta}>
          {cta.text}
        </BaseLink>
      </div>
    </div>
  );
}

export default memo(CommunityCarousel);
